/**
 * useCommunity Hooks
 *
 * React hooks for the three community dashboard card endpoints:
 *   - useCommunityImpact  → /community/{companyId}/impact
 *   - useUpcomingEvents   → /community/{companyId}/events
 *   - useCommunityUpdates → /community/{companyId}/updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getCommunityImpact,
  getUpcomingEvents,
  getCommunityUpdates,
} from '@/services/api/communityService';
import { cacheService } from '@/services/cache/cacheService';
import { useCompany } from './useCompany';
import type {
  CommunityImpactResponse,
  UpcomingEventsResponse,
  CommunityUpdatesResponse,
} from '@/types/community.types';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

interface BaseOptions {
  companyId?: number;
  autoFetch?: boolean;
  enableCache?: boolean;
  cacheTTL?: number;
}

interface BaseResult<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// useCommunityImpact
// ---------------------------------------------------------------------------

export type UseCommunityImpactOptions = BaseOptions;
export type UseCommunityImpactResult = BaseResult<CommunityImpactResponse>;

/**
 * Hook to fetch community impact metrics.
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useCommunityImpact();
 * ```
 */
export function useCommunityImpact(options?: UseCommunityImpactOptions): UseCommunityImpactResult {
  const { companyIdNumber } = useCompany();
  const {
    companyId = companyIdNumber,
    autoFetch = true,
    enableCache = true,
    cacheTTL = 15 * 60 * 1000, // 15 minutes
  } = options || {};

  const [data, setData] = useState<CommunityImpactResponse | null>(null);
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [error, setError] = useState<Error | null>(null);
  const isFetchingRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (!companyId || isFetchingRef.current) return;

    const cacheKey = `community:impact:${companyId}`;
    setIsLoading(true);
    setError(null);
    isFetchingRef.current = true;

    try {
      if (enableCache) {
        const cached = cacheService.get<CommunityImpactResponse>(cacheKey);
        if (cached) {
          setData(cached);
          return;
        }
      }

      const response = await getCommunityImpact(companyId);
      setData(response);

      if (enableCache) {
        cacheService.set(cacheKey, response, { ttl: cacheTTL });
      }
    } catch (err) {
      const fetchError = err instanceof Error ? err : new Error('Failed to fetch community impact');
      setError(fetchError);
      console.error('[useCommunityImpact] Error:', fetchError);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [companyId, enableCache, cacheTTL]);

  useEffect(() => {
    if (autoFetch && companyId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, autoFetch]);

  return { data, isLoading, error, refetch: fetchData };
}

// ---------------------------------------------------------------------------
// useUpcomingEvents
// ---------------------------------------------------------------------------

export type UseUpcomingEventsOptions = BaseOptions;
export type UseUpcomingEventsResult = BaseResult<UpcomingEventsResponse>;

/**
 * Hook to fetch upcoming events.
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useUpcomingEvents();
 * ```
 */
export function useUpcomingEvents(options?: UseUpcomingEventsOptions): UseUpcomingEventsResult {
  const { companyIdNumber } = useCompany();
  const {
    companyId = companyIdNumber,
    autoFetch = true,
    enableCache = true,
    cacheTTL = 15 * 60 * 1000,
  } = options || {};

  const [data, setData] = useState<UpcomingEventsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [error, setError] = useState<Error | null>(null);
  const isFetchingRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (!companyId || isFetchingRef.current) return;

    const cacheKey = `community:events:${companyId}`;
    setIsLoading(true);
    setError(null);
    isFetchingRef.current = true;

    try {
      if (enableCache) {
        const cached = cacheService.get<UpcomingEventsResponse>(cacheKey);
        if (cached) {
          setData(cached);
          return;
        }
      }

      const response = await getUpcomingEvents(companyId);
      setData(response);

      if (enableCache) {
        cacheService.set(cacheKey, response, { ttl: cacheTTL });
      }
    } catch (err) {
      const fetchError = err instanceof Error ? err : new Error('Failed to fetch upcoming events');
      setError(fetchError);
      console.error('[useUpcomingEvents] Error:', fetchError);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [companyId, enableCache, cacheTTL]);

  useEffect(() => {
    if (autoFetch && companyId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, autoFetch]);

  return { data, isLoading, error, refetch: fetchData };
}

// ---------------------------------------------------------------------------
// useCommunityUpdates
// ---------------------------------------------------------------------------

export type UseCommunityUpdatesOptions = BaseOptions;
export type UseCommunityUpdatesResult = BaseResult<CommunityUpdatesResponse>;

/**
 * Hook to fetch community updates.
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useCommunityUpdates();
 * ```
 */
export function useCommunityUpdates(
  options?: UseCommunityUpdatesOptions
): UseCommunityUpdatesResult {
  const { companyIdNumber } = useCompany();
  const {
    companyId = companyIdNumber,
    autoFetch = true,
    enableCache = true,
    cacheTTL = 15 * 60 * 1000,
  } = options || {};

  const [data, setData] = useState<CommunityUpdatesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [error, setError] = useState<Error | null>(null);
  const isFetchingRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (!companyId || isFetchingRef.current) return;

    const cacheKey = `community:updates:${companyId}`;
    setIsLoading(true);
    setError(null);
    isFetchingRef.current = true;

    try {
      if (enableCache) {
        const cached = cacheService.get<CommunityUpdatesResponse>(cacheKey);
        if (cached) {
          setData(cached);
          return;
        }
      }

      const response = await getCommunityUpdates(companyId);
      setData(response);

      if (enableCache) {
        cacheService.set(cacheKey, response, { ttl: cacheTTL });
      }
    } catch (err) {
      const fetchError =
        err instanceof Error ? err : new Error('Failed to fetch community updates');
      setError(fetchError);
      console.error('[useCommunityUpdates] Error:', fetchError);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [companyId, enableCache, cacheTTL]);

  useEffect(() => {
    if (autoFetch && companyId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, autoFetch]);

  return { data, isLoading, error, refetch: fetchData };
}
